import { COPY_GROUPS, type CopyGroupId } from "./lazy.ts";

/**
 * Which pieces of copy a page's address brings along. The browser fetches them when a navigation starts, so a
 * screen's words travel with its code. No dependencies.
 */

/** The group each first part of a key lives in: `questions.title` → "questions". */
export const GROUP_OF_KEY: ReadonlyMap<string, CopyGroupId> = new Map(
  (Object.keys(COPY_GROUPS) as CopyGroupId[]).flatMap((id) =>
    COPY_GROUPS[id].keys.map((key) => [key, id] as const),
  ),
);

/** Words in an address that stand for a group of screens the key names don't say (staff pages). */
const ALIASES: Record<string, string> = { sysadmin: "staff" };

/**
 * Pages that also show another group's pieces. A word of an address maps to every group its screens read.
 */
const ALSO: Record<string, CopyGroupId[]> = {
  // the understanding and the study show the company's sources
  understanding: ["understand", "data"],
  study: ["understand", "data"],
  // the Data page shows the code connect card (its words are their own piece)
  data: ["code"],
  // an insight's page shows its result the way a question does, and the Share button
  insights: ["questions", "sharingUi"],
  // the Share button on a list, a page and a measure; the page a public link opens
  lists: ["sharingUi"],
  pages: ["sharingUi"],
  measures: ["sharingUi"],
  shared: ["sharingUi"],
  // Developers: keys and webhooks
  keys: ["webhooks"],
  // "Make a list" under a table in an answer, and the Share button
  threads: ["lists", "sharingUi"],
  // Notifications: what Adea sends you and when
  notifications: ["delivery"],
  // the consent page for Adea's staff
  "staff-access": ["staff"],
  // an invitation to join a company
  invite: ["settings"],
  // a shop or a source approving the connection
  connect: ["data", "code"],
};

/** Home (`/`, an address with no words): its first run shows the sources being connected and Adea learning live. */
const HOME: CopyGroupId[] = ["data", "understand", "settingsBrand"];

/** The groups for the words of an address: `/questions/screenshot/…` → questions and screenshot; `/` → Home's. */
export function groupsForPath(words: Iterable<string>): CopyGroupId[] {
  const found = new Set<CopyGroupId>();
  let any = false;
  for (const raw of words) {
    const word = ALIASES[raw.replace(/^[_$]+/, "")] ?? raw.replace(/^[_$]+/, "");
    if (word.length < 3) continue;
    any = true;
    for (const id of ALSO[word] ?? []) found.add(id);
    for (const [key, id] of GROUP_OF_KEY) if (key.toLowerCase().startsWith(word.toLowerCase())) found.add(id);
  }
  if (!any) for (const id of HOME) found.add(id);
  return [...found];
}
